<template>
    <div class="macro">
        <p>
            <a class="button copy-button is-info" @click="copyMacroText()">Copy Macro Text</a>
            <a class="button copy-button is-info ml-2" @click="modalOpen = true">Input Macro Text</a>
        </p>
        <pre>{{ macroText }}</pre>
        <div class="modal" :class="{ 'is-active': modalOpen }">
            <div class="modal-background" @click.stop="modalOpen = false"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">Input Macro Text</p>
                    <button class="delete" aria-label="close" @click="modalOpen = false"></button>
                </header>
                <section class="modal-card-body">
                    <p class="mb-4">Paste in a macro generated by this app (or manually generated)
                        and it will try to read it back into the form for editing. This
                        is not perfect and may not work 100% of the time.</p>
                    <p class="mb-4"><strong>Currently, the Psion tab is not supported. This may be added in the future.</strong></p>
                    <textarea class="textarea" name="macro-input" id="macro-input" v-model="macroInput"></textarea>
                </section>
                <footer class="modal-card-foot">
                    <button class="button is-success" @click="parseTags()">Save changes</button>
                    <button class="button" @click="modalOpen = false">Cancel</button>
                </footer>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
// import { filter } from 'minimatch';

export default {
    props: {

    },
    data: function() {
        return {
            templateTag: '&{template:dnd4epower}',
            modalOpen: false,
            macroInput: ""
        };
    },
    computed: {
        ...mapState({
            basic: state => state.basic,
            attack: state => state.attack,
            damage: state => state.damage,
            custom: state => state.custom,
            monk: state => state.monk,
            psion: state => state.psion,
        }),
        macroText() {
            let app = this;

            let allTags = [
                this.templateTag,
                this.generateTag(this.basic.type, '1'),
                this.generateTag('name', this.basic.name),
                this.generateTag('emote', this.basic.emote),
                this.generateTag('class', this.basic.powerClass),
                this.generateTag('level', this.basic.level),
                this.typeAndKeywordsTags(),
                this.actionAndRangeTags(),
                this.generateTag('attacktechnique', this.monk.attackTechniqueName),
                this.generateTag('requirement', this.basic.requirement),
                this.generateTag('trigger', this.basic.trigger),
                this.generateTag('beforespecial', this.basic.beforeSpecial),
                this.generateTag('beforeeffect', this.basic.beforeEffect),
                this.attack.type == 'multiple' ? this.generateTag('primarytarget', this.basic.target) : this.generateTag('target', this.basic.target),
                this.firstAttackTags(),
                this.areaAttackTags(),
                this.firstDamageTags(),
                this.firstCriticalDamageTags(),
                this.generateTag('hiteffect', this.attack.primary.hitEffect),
                this.generateTag('miss', this.attack.missEffect),
                this.generateTag('secondarytarget', this.attack.secondary.target),
                this.secondAttackTags(),
                this.secondDamageTags(),
                this.secondCriticalDamageTags(),
                this.generateTag('secondaryhiteffect', this.attack.secondary.hitEffect),
                this.generateTag('tertiarytarget', this.attack.tertiary.target),
                this.thirdAttackTags(),
                this.thirdDamageTags(),
                this.thirdCriticalDamageTags(),
                this.generateTag('tertiaryhiteffect', this.attack.tertiary.hitEffect),
                this.psion.hasAugment ? this.generateTag('augmenttoggle', '[[ ?{Augment 0, 1, 2, 4, 6|0} ]]') : false,
                this.augmentTags(),
                this.generateTag('effect', this.basic.effect),
                this.generateTag('firstfailedsave', this.attack.failedSaves.first),
                this.generateTag('secondfailedsave', this.attack.failedSaves.second),
                this.generateTag('thirdfailedsave', this.attack.failedSaves.third),
                this.generateTag('movetechnique', this.monk.moveTechnique.name),
                this.movementTechniqueActionAndRangeTags(),
                this.generateTag('mttarget', this.monk.moveTechnique.target),
                this.generateTag('mteffect', this.monk.moveTechnique.effect),
                this.generateTag('special', this.basic.special),
            ];

            this.custom.custom.forEach((item) => {
                if(item.label && item.value) {
                    allTags.push(app.generateTag(`**${item.label}:**`, item.value));
                }
            });

            let filteredTags = allTags.filter((tag) => tag && tag != '');

            return filteredTags.join(' ').trim();
        },
        powerTypeTag() {
            return `{{${this.type}=1}}`;
        },
        tagToMutator() {
            return {
                'name': this.basic.name
            }
        },
    },
    methods: {
        ...mapActions(["setFromParsedTags"]),
        generateTag(name, value) {
            return value && value != '' ? `{{${name}=${value}}}` : false;
        },
        typeAndKeywordsTags() {
            let typeTag = this.basic.typeName != '' ? (this.basic.keywords != '' ? `{{type=${this.basic.typeName} ♦ }}` : `{{type=${this.basic.typeName}}}`) : '';
            let keywordTag = this.basic.keywords != '' ? `{{keywords=${this.basic.keywords}}}` : '';

            return [ typeTag, keywordTag ].join(' ').trim();
        },
        actionAndRangeTags() {
            let actionTag = this.basic.actionType != '' ? (this.basic.range != '' ? `{{action=${this.basic.actionType} ♦ }}` : `{{action=${this.basic.actionType}}}`) : '';
            let rangeTag = this.basic.range != '' ? `{{range=${this.basic.range}}}` : '';

            return [ actionTag, rangeTag ].join(' ').trim();
        },
        movementTechniqueActionAndRangeTags() {
            let actionTag = this.monk.moveTechnique.type != '' ? (this.monk.moveTechnique.range != '' ? `{{mttype=${this.monk.moveTechnique.type} ♦ }}` : `{{mttype=${this.monk.moveTechnique.type}}}`) : '';
            let rangeTag = this.monk.moveTechnique.range != '' ? `{{mtrange=${this.monk.moveTechnique.range}}}` : '';

            return [ actionTag, rangeTag ].join(' ').trim();
        },
        getAttackTag(attackProps, attackPrefix, attackSuffix) {
            const baseDie = attackProps.critRange && attackProps.critRange != 20 ? `1d20cs>${attackProps.critRange}` : '1d20';
            let attackRollParts = [ baseDie ];

            if(attackProps.bonusSource != 'static') {
                attackRollParts.push(`[[ @{${attackProps.stat.toLowerCase()}-mod} ]] [${attackProps.stat} modifier]`);
                attackRollParts.push(`[[@{halflevel}]] [half level]`);
            }

            if(attackProps.weaponNumber != '' && (attackProps.bonusSource == 'weapon' || attackProps.bonusSource == 'implement')) {
                attackRollParts.push(`[[ @{weapon-${attackProps.weaponNumber}-attack} ]] [${attackProps.bonusSource} attack bonus]`);
            }

            if(attackProps.staticBonus != '') {
                attackRollParts.push(`[[ ${attackProps.staticBonus} ]] [static bonus]`);
            }

            let attackRoll = attackRollParts.join(' + ');

            return `{{${attackPrefix}attack${attackSuffix}=[[ ${attackRoll} ]] vs **${attackProps.defense}**}}`
        },
        firstAttackTags() {
            return this.attack.isAttack ? this.getAttackTag(this.attack.primary, '', '') : false;
        },
        secondAttackTags() {
            return this.attack.isAttack && this.attack.type == 'multiple' ? this.getAttackTag(this.attack.secondary, 'secondary', '') : false;
        },
        thirdAttackTags() {
            return this.attack.isAttack && this.attack.type == 'multiple' && this.attack.tertiary.hasTertiary ? this.getAttackTag(this.attack.tertiary, 'tertiary', '') : false;
        },
        areaAttackTags() {
            if(!this.attack.isAttack || this.attack.type != 'area' || !this.attack.maxTargets || !this.isNumeric(this.attack.maxTargets)) {
                return false;
            }

            let finalTags = [ '{{multiattacktoggle=[[?{Number of Attacks|2}]]}}' ];
            let attacks = parseInt(this.attack.maxTargets) > 20 ? 20 : parseInt(this.attack.maxTargets);

            // start index at 1, because we actually want one less attack than the number
            for(let i=1;i<attacks;i++) {
                finalTags.push(this.getAttackTag(this.attack.primary, 'multi', i + 1));
            }

            return finalTags.join(' ');
        },
        getDamageTags(attackProps, damageProps, isCrit, tagPrefix) {
            let damageParts = [];

            if(attackProps.bonusSource == 'weapon') {
                if(damageProps.diceSides != '') {
                    let baseDamage =
                        isCrit ?
                            `(${damageProps.diceQuantity} * @{weapon-${attackProps.weaponNumber}-dice}) [maximum weapon damage]` :
                            damageProps.brutalValue != '' ?
                                `${damageProps.diceQuantity}d@{weapon-${attackProps.weaponNumber}-dice}r<${damageProps.brutalValue}` :
                                `${damageProps.diceQuantity}d@{weapon-${attackProps.weaponNumber}-dice}`;
                    damageParts.push(baseDamage);
                }
            } else if(damageProps.diceSides != '' && damageProps.diceQuantity != '') {
                let baseDamage =
                    isCrit ?
                        `(${damageProps.diceQuantity} * ${damageProps.diceSides}) [maximum power damage]` :
                        damageProps.brutalValue != '' ?
                            `${damageProps.diceQuantity}d${damageProps.diceSides}r<${damageProps.brutalValue}` :
                            `${damageProps.diceQuantity}d${damageProps.diceSides}`;
                damageParts.push(baseDamage);
            }

            if(damageProps.addStatMod) {
                damageParts.push(`[[ @{${attackProps.stat.toLowerCase()}-mod} ]] [${attackProps.stat} modifier]`);
            }

            if(attackProps.weaponNumber != '' && (attackProps.bonusSource == 'weapon' || attackProps.bonusSource == 'implement')) {
                damageParts.push(`[[ @{weapon-${attackProps.weaponNumber}-damage} ]] [${attackProps.bonusSource} damage bonus]`);
                if(isCrit) {
                    damageParts.push(`(@{weapon-${attackProps.weaponNumber}-enh}d@{weapon-${attackProps.weaponNumber}-per-plus}) [critical bonus damage]`);
                }
            }

            if(damageProps.staticBonus != '') {
                damageParts.push(`[[ ${damageProps.staticBonus} ]] [static bonus]`);
            }

            let damageStuff = damageParts.join(' + ');
            let tagName = isCrit ? 'critical' : 'damage';

            return damageProps.type != '' ? `{{${tagPrefix}${tagName}=[[ ${damageStuff} ]] ${damageProps.type} damage.}}` : `{{${tagPrefix}${tagName}=[[ ${damageStuff} ]] damage.}}`;
        },
        firstDamageTags() {
            return this.damage.primary.hasDamage ? this.getDamageTags(this.attack.primary, this.damage.primary, false, '') : false;
        },
        firstCriticalDamageTags() {
            return this.damage.primary.hasDamage ? this.getDamageTags(this.attack.primary, this.damage.primary, true, '') : false;
        },
        secondDamageTags() {
            return this.damage.secondary.hasDamage ? this.getDamageTags(this.attack.secondary, this.damage.secondary, false, 'secondary') : false;
        },
        secondCriticalDamageTags() {
            return this.damage.secondary.hasDamage ? this.getDamageTags(this.attack.secondary, this.damage.secondary, true, 'secondary') : false;
        },
        thirdDamageTags() {
            return this.damage.tertiary.hasDamage ? this.getDamageTags(this.attack.tertiary, this.damage.tertiary, false, 'tertiary') : false;
        },
        thirdCriticalDamageTags() {
            return this.damage.tertiary.hasDamage ? this.getDamageTags(this.attack.tertiary, this.damage.tertiary, true, 'tertiary') : false;
        },
        augmentTags() {
            let keys = Object.keys(this.psion.augments);
            let tags = [];
            keys.forEach((augKey) => {
                let thisItem = this.psion.augments[augKey];
                if(this.psion.augments[augKey].exists) {
                    tags.push(thisItem.range ? `{{augment${augKey}range=${thisItem.range}}}` : false);
                    tags.push(thisItem.target ? `{{augment${augKey}target=${thisItem.target}}}` : false);
                    tags.push(thisItem.attack.isUnique ? this.getAttackTag(thisItem.attack, `augment${augKey}`, '') : `{{augment${augKey}attack=As above.}}`);

                    if(this.psion.augments[augKey].attack.isArea) {
                        tags.push(`{{augment${augKey}multitoggle=[[?{Augment ${augKey}: Number of Attacks|2}]]}}`);
                        for(let i=2;i<=9;i++) {
                            tags.push(this.getAttackTag(thisItem.attack, `augment${augKey}multi`, i));
                        }
                    }

                    tags.push(thisItem.damage.hasDamage ? this.getDamageTags(thisItem.attack, thisItem.damage, false, `augment${augKey}`) : false);
                    tags.push(thisItem.damage.hasDamage ? this.getDamageTags(thisItem.attack, thisItem.damage, true, `augment${augKey}`) : false);
                    tags.push(thisItem.attack.hitEffect ? `{{augment${augKey}hiteffect=${thisItem.attack.hitEffect}}}` : false);
                }
            });

            let filteredTags = tags.filter((tag) => tag && tag != '');

            return filteredTags.join(' ');
        },
        isNumeric(maybeNumber) {
            return !isNaN(parseInt(maybeNumber));
        },
        copyMacroText() {
            const macroText = this.macroText;

            let textArea = document.createElement("textarea");
            textArea.value = macroText;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            document.execCommand('copy');

            document.body.removeChild(textArea);
        },
        parseTags() {
            const data = this.macroInput;
            const tags = [...data.matchAll(/{{([\s|\S]+?)}}/g)].map((el) => el[1]);

            this.setFromParsedTags(tags);

            this.macroInput = "";
            this.modalOpen = false;
        }
    }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
    .macro {
        margin-top: 2rem;
    }

    pre {
        margin-top: 2rem;
        white-space: pre-wrap;
        overflow-x: hidden;
    }

    .ml-2 {
        margin-left: 0.5rem;
    }
</style>
